import moment from 'moment'
import { NetworkStatus } from '@apollo/client'
import { FteImportProcessDocument } from 'generated/resources/graphql'

import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from '@factorialco/deprecated-design-system'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

export const usePendingImportProcesses = ({
  fteEnabled,
  employeeIds,
  isEmployeeCostsPage,
}: {
  fteEnabled: boolean
  employeeIds?: number[]
  isEmployeeCostsPage: boolean
}) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const [fetchPendingImportProcesses, { data, networkStatus }] = useLazyQuery(
    FteImportProcessDocument,
    {
      variables: { companyId },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
    }
  )

  const loading = networkStatus === NetworkStatus.loading

  useUnsafeEffect(() => {
    if (fteEnabled && !!employeeIds?.length && isEmployeeCostsPage) {
      fetchPendingImportProcesses()
    }
  }, [fteEnabled, employeeIds, isEmployeeCostsPage, fetchPendingImportProcesses])

  const hasPendingFteImport = useUnsafeMemo(() => {
    if (!data) return false
    if (loading) return false

    return !!data.universalImporter.importProcessesConnection.nodes.find(
      ({ importType, metadata, updatedAt }) => {
        if (importType !== 'working_time_percentage_importer') return false
        if (moment(updatedAt).isBefore(moment().subtract(1, 'hour'))) return false

        const employeeIdsMetadata = metadata?.find(({ key }) => key === 'employee_ids')
        if (!employeeIdsMetadata?.values) return false

        return employeeIdsMetadata.values.join(',') === employeeIds?.join(',')
      }
    )
  }, [data, employeeIds, loading])

  return {
    data,
    loading,
    hasPendingFteImport,
  }
}
